import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Listing : famille d'entités "annonce".
 *
 * Offre publiée portant sur un objet UNIQUE (un bien précis, pas un article de
 * catalogue) à vendre, à louer ou à faire adopter, avec ses canaux de diffusion
 * et son statut. Construite sur le modèle "famille + type" de la bible
 * (patron : asset). Le socle ne connaît pas l'annonce concrète (vente d'un T3,
 * adoption d'un chat) : il connaît la famille générique `listing`. Le
 * vocabulaire métier est porté par le champ `type` (chaîne libre déclarée par
 * l'app : "vente", "location", "adoption"), l'état de diffusion par `status`.
 *
 * Le sujet de l'annonce est polymorphe (le `subjectRef` de la fiche) : une
 * annonce porte sur UN `place` (bien immobilier) OU UN `asset` (objet, animal).
 * D'où deux ids optionnels `placeId` / `assetId`, jamais requis individuellement
 * (c'est l'un OU l'autre selon le cas).
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma).
 * Les liens opportunity (candidats et acquéreurs suivis) et document (photos,
 * diagnostics) ne vivent PAS ici : c'est l'autre entité qui porte un
 * `listingId` (une annonce a plusieurs candidats et plusieurs pièces jointes).
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille listing (hors champs standard du socle). */
export const listingCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "vente", "location", "adoption"). */
  type: z.string().min(1),
  /** Bien immobilier annoncé (référence `place`), branche du sujet polymorphe. Optionnel. */
  placeId: z.string().min(1).optional(),
  /** Objet ou animal proposé (référence `asset`), branche du sujet polymorphe. Optionnel. */
  assetId: z.string().min(1).optional(),
  /** Prix affiché de l'offre (absent pour une adoption). Optionnel. */
  price: z.number().optional(),
  /** Canaux de diffusion où l'annonce est publiée (ex. "vitrine", "portail"). Optionnel. */
  publishedChannels: z.array(z.string()).optional(),
  /** État courant, chaîne libre pilotée par l'app (ex. "brouillon", "publiee", "vendue"). Optionnel. */
  status: z.string().min(1).optional(),
  /** Mandat qui autorise la diffusion (référence `agreement`). Optionnel. */
  agreementId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille listing (champs standard + champs métier). */
export const ListingSchema = z.object({
  ...baseEntityFields,
  ...listingCoreFields,
});

/** Type d'un listing. */
export type Listing = z.infer<typeof ListingSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const ListingCreateInputSchema = z.object({
  ...listingCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un listing. */
export type ListingCreateInput = z.infer<typeof ListingCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const ListingUpdateInputSchema = z
  .object({ ...listingCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'un listing. */
export type ListingUpdateInput = z.infer<typeof ListingUpdateInputSchema>;
